import * as angular from 'angular';
import * as _ from 'lodash';
import { TransitionService } from '@uirouter/angularjs';

import { ContextualField, ComponentDefinition, multiSelects, PropertyType, IExtraIcon, ApplicationUserViewModel, Miniature, IExtraButton, IErrorReporting } from '../../models';
import { IStoreService } from '../../services';
import { Store, StoreElement } from '../../stateManagement';

class ContextualControlController implements angular.IComponentController, angular.IOnChanges {
    errors: IErrorReporting[];
    fields: ContextualField[];
    fieldsGroupings: ContextualField[][];
    formController: angular.IFormController;
    isLoading: boolean;
    isSaving: boolean;
    miniatures: Miniature[];
    multiSelects: PropertyType[];
    users: ApplicationUserViewModel[] = [];

    readonly extraButtons: IExtraButton[];
    readonly extraIcon: IExtraIcon;
    readonly extraValidationFields: ContextualField[];
    readonly hasPropertyTypes: boolean;
    readonly icons: boolean;
    readonly isCancelManaged: boolean;
    readonly isDirtyFromOutside: boolean;
    readonly isInvalidFromOutside: boolean;
    readonly isTrackingDisabled: boolean;
    readonly item: Object;
    readonly modernDesign: boolean;
    readonly modifiedBy: ApplicationUserViewModel;
    readonly modifiedOn: string;
    readonly onCancel: () => void;
    readonly save: () => angular.IPromise<Object>;
    readonly selectedMiniature: Miniature;
    readonly setAutocomplete: string;
    readonly templateTitle: string;

    private _pristine: Object;
    private _unsubscribe: () => void;

    static $inject = [
        '$scope',
        '$timeout',
        '$transitions',
        'storeService'
    ];

    constructor(
        private _$scope: angular.IScope,
        private _$timeout: angular.ITimeoutService,
        private _$transition: TransitionService,
        private _storeService: IStoreService
    ) {
        this.isValid = this.isValid.bind(this);
    }

    $onInit() {
        this.isLoading = true;
        this.multiSelects = multiSelects;
        this._$scope.$watchCollection(() => this.fields, () => {
            // timeout for execution after digest and formController refresh
            this._$timeout(() => {
                this.checkValidity();
            });
            this.fieldsGroupings = this.createGroupings(this.fields);
            this.errors = this.fields?.concat(this.extraValidationFields || []);
        });
        this._$transition.onStart({}, () => {
            if (this.formController && this.formController.$dirty) {
                this.cancel();
            }
        });
        if (!this.isTrackingDisabled) {
            this._unsubscribe = this._storeService.register(
                store => this.refresh(store),
                [StoreElement.Users]
            );
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['item']) {
            if (this.item) {
                this.savePristine();
                this.checkValidity();
            } else {
                this._pristine = null;
            }
        }
        if (changesObj['extraValidationFields']) {
            if (this.extraValidationFields) {
                this.errors = this.fields.concat(this.extraValidationFields);
            } else {
                this.errors = this.fields;
            }
        }
    }

    $onDestroy() {
        if (this._unsubscribe) {
            this._unsubscribe();
        }
    }

    refresh(store: Store) {
        this.users = Object.values<ApplicationUserViewModel>(store.users.dictionary);
        this.isLoading = false;
    }

    wrappedSave() {
        if (this.save) {
            this.isSaving = true;
            this.save()
                .then((isFormDirty: boolean) => {
                    if (!isFormDirty) {
                        this.formController.$setPristine();
                        this.savePristine();
                    }
                })
                .finally(() => this.isSaving = false);
        }
    }

    model(field: ContextualField) {
        if (field.extraData && field.extraData.subProperty) {
            const splittedProperty = field.extraData.subProperty.split('.');
            if (splittedProperty && splittedProperty.length >= 1) {
                let property = this.item;
                for (let i = 0; i < splittedProperty.length; i++) {
                    if (property[splittedProperty[i]]) {
                        property = property[splittedProperty[i]];
                    } else {
                        property = undefined;
                        break;
                    }
                }
                return property;
            }
        }
        return this.item;
    }

    cancel() {
        if (this._pristine) {
            angular.copy(this._pristine, this.item);
        }
        this.formController.$setPristine();
        if (this.onCancel) {
            this.onCancel();
        }
    }

    isValid(field: ContextualField) {
        if (!!field.isValid && this.formController[field.propertyName]) {
            const value = this.formController[field.propertyName].$viewValue;
            const result = field.isValid(value);
            for (const key of Object.keys(result)) {
                this.formController[field.propertyName].$setValidity(key, result[key]);
            }
        }
    }

    onChange(value: Object) {
        return (field: ContextualField) => {
            this.isValid(field);
            if (field.onChange) {
                try {
                    const tmp = this.formController[field.propertyName];
                    const viewValue = tmp ? tmp.$viewValue : null;
                    field.onChange(viewValue, value);
                }
                catch (e) {
                    console.error(e);
                }
            }
        };
    }

    isSaveButtonDisabled() {
        return !!this.formController?.$invalid
            || !!this.isInvalidFromOutside
            || (!this.formController.$dirty && !this.isDirtyFromOutside)
            || !!this.isSaving
            || !!this.formController?.popoverEdit;
    }

    private checkValidity() {
        if (this.formController && this.fields) {
            this.fields
                .filter(f => !!f.isValid)
                .forEach(f => this.isValid(f));
        }
    }

    private savePristine() {
        if (!this.isCancelManaged) {
            try {
                this._pristine = angular.copy(this.item);
            } catch (error) {
                console.warn('Failed to create a pristine copy of the selected item. Cancel has to be managed manually.');
                console.warn(error);
            }
        }
    }

    private createGroupings(fields: ContextualField[]) {
        let currentGroupingIndex = 0;
        const fieldsGroupings: ContextualField[][] = [];

        if (fields?.length) {
            fields.forEach(field => {
                if (!fieldsGroupings[currentGroupingIndex]) {
                    fieldsGroupings[currentGroupingIndex] = [];
                }
                fieldsGroupings[currentGroupingIndex].push(field);
                if (!!field?.addSeparatorAfter) {
                    currentGroupingIndex++;
                }
            });
        }
        return fieldsGroupings;
    }
}

export const wtContextualControl: ComponentDefinition = {
    componentId: 'wtContextualControl',
    controller: ContextualControlController,
    controllerAs: 'vm',
    template: require('./contextualControl.component.html'),
    bindings: {
        extraButtons: '<?',
        extraIcon: '<?',
        extraValidationFields: '<?',
        fields: '=',
        formController: '=?',
        hasPropertyTypes: '<?',
        icons: '<?',
        isCancelManaged: '<?',
        isDirtyFromOutside: '<?',
        isInvalidFromOutside: '<?',
        isTrackingDisabled: '<?',
        item: '<',
        miniatures: '=?',
        modernDesign: '<?',
        onCancel: '&?',
        save: '&',
        selectedMiniature: '=?',
        setAutocomplete: '<?',
        templateTitle: '<?'
    },
    transclude: {
        searchBar: '?searchBar',
        extraProperties: '?extraProperties'
    }
};